import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../hooks/useAuth'
import { FormField } from '../shared/FormField'
import { Button } from '../shared/Button'
import { supabase } from '../../lib/supabase'

/**
 * ChangeEmailForm Component
 *
 * Handles email change requests with security verification.
 * Requires current password for authentication before allowing email change.
 *
 * Features:
 * - Current email display (read-only)
 * - New email validation
 * - Password verification requirement
 * - Success/error messaging
 * - Loading states
 * - Email verification flow information
 */
function ChangeEmailForm({ currentEmail }) {
  const { t } = useTranslation('profile')
  const { updateEmail } = useAuth()

  // Form state
  const [formData, setFormData] = useState({
    newEmail: '',
    currentPassword: ''
  })

  // UI state
  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  const [showPassword, setShowPassword] = useState(false)
  const [updatedEmail, setUpdatedEmail] = useState(null)

  // Email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

  // Handle input changes
  const handleChange = useCallback((e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))

    // Clear specific field error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }))
    }

    // Clear submit error when user modifies form
    if (submitError) {
      setSubmitError(null)
    }
  }, [errors, submitError])

  // Validate form
  const validateForm = useCallback(() => {
    const newErrors = {}

    // Validate new email
    if (!formData.newEmail) {
      newErrors.newEmail = t('profile:profile.email.errors.required')
    } else if (!emailRegex.test(formData.newEmail)) {
      newErrors.newEmail = t('profile:profile.email.errors.invalid')
    } else if (formData.newEmail === currentEmail) {
      newErrors.newEmail = t('profile:profile.email.errors.sameEmail')
    }

    // Validate password
    if (!formData.currentPassword) {
      newErrors.currentPassword = t('profile:profile.email.errors.passwordRequired')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }, [formData, currentEmail, t])

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)
    setSubmitError(null)
    setShowSuccess(false)

    try {
      // First, verify the current password
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: currentEmail,
        password: formData.currentPassword
      })

      if (signInError) {
        setErrors({ currentPassword: t('profile:profile.email.errors.invalidPassword') })
        setIsSubmitting(false)
        return
      }

      // Update the email
      const { error: updateError } = await updateEmail(formData.newEmail)

      if (updateError) {
        throw updateError
      }

      // Success - Store email before clearing form
      setUpdatedEmail(formData.newEmail)
      setShowSuccess(true)
      setFormData({ newEmail: '', currentPassword: '' })

      // Hide success message after 10 seconds
      setTimeout(() => {
        setShowSuccess(false)
      }, 10000)

    } catch (error) {
      console.error('Email update error:', error)
      setSubmitError(error.message || t('profile:profile.email.errors.updateFailed'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl">
      {/* Success Message */}
      {showSuccess && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
          <div className="flex">
            <svg className="h-5 w-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <p className="font-medium">{t('profile:profile.email.success.title')}</p>
              <p className="text-sm mt-1">
                {t('profile:profile.email.success.message', { email: updatedEmail })}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {submitError && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
          <div className="flex">
            <svg className="h-5 w-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <p className="font-medium">{t('profile:profile.email.error.title')}</p>
              <p className="text-sm mt-1">{submitError}</p>
            </div>
          </div>
        </div>
      )}

      {/* Current Email Display */}
      <FormField
        label={t('profile:profile.email.currentEmail.label')}
        helperText={t('profile:profile.email.currentEmail.helper')}
      >
        <input
          type="email"
          value={currentEmail || ''}
          disabled
          className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
          aria-label={t('profile:profile.email.currentEmail.label')}
        />
      </FormField>

      {/* New Email Input */}
      <FormField
        label={t('profile:profile.email.newEmail.label')}
        error={errors.newEmail}
        helperText={!errors.newEmail && t('profile:profile.email.newEmail.helper')}
        required
      >
        <input
          type="email"
          name="newEmail"
          value={formData.newEmail}
          onChange={handleChange}
          placeholder={t('profile:profile.email.newEmail.placeholder')}
          disabled={isSubmitting}
          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors
            ${errors.newEmail
              ? 'border-red-300 bg-red-50'
              : 'border-gray-300 hover:border-gray-400'
            }
            ${isSubmitting ? 'bg-gray-50 cursor-not-allowed' : 'bg-white'}`}
          aria-invalid={!!errors.newEmail}
        />
      </FormField>

      {/* Current Password Input */}
      <FormField
        label={t('profile:profile.email.password.label')}
        error={errors.currentPassword}
        helperText={!errors.currentPassword && t('profile:profile.email.password.helper')}
        required
      >
        <div className="relative">
          <input
            type={showPassword ? 'text' : 'password'}
            name="currentPassword"
            value={formData.currentPassword}
            onChange={handleChange}
            placeholder={t('profile:profile.email.password.placeholder')}
            disabled={isSubmitting}
            className={`w-full px-4 py-2 pr-12 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors
              ${errors.currentPassword
                ? 'border-red-300 bg-red-50'
                : 'border-gray-300 hover:border-gray-400'
              }
              ${isSubmitting ? 'bg-gray-50 cursor-not-allowed' : 'bg-white'}`}
            aria-invalid={!!errors.currentPassword}
          />

          {/* Password Toggle Button */}
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none focus:text-gray-700 transition-colors"
            aria-label={showPassword ? t('profile:security.hidePassword') : t('profile:security.showPassword')}
          >
            {showPassword ? (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            )}
          </button>
        </div>
      </FormField>

      {/* Information Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <svg className="h-5 w-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">{t('profile:profile.email.info.title')}</p>
            <ul className="space-y-1 text-blue-700">
              <li>• {t('profile:profile.email.info.verification')}</li>
              <li>• {t('profile:profile.email.info.loginUpdate')}</li>
              <li>• {t('profile:profile.email.info.notifications')}</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end pt-4">
        <Button
          type="submit"
          variant="primary"
          size="md"
          loading={isSubmitting}
          disabled={isSubmitting || !formData.newEmail || !formData.currentPassword}
        >
          {isSubmitting ? t('profile:profile.email.button.updating') : t('profile:profile.email.button.update')}
        </Button>
      </div>
    </form>
  )
}

export default ChangeEmailForm